import useIsHydrated from '@klaviyo-mkt/gatsby-components/hooks/useIsHydrated';
import { useTranslation } from '@klv/hooks/useTranslation';
import Icon from '@klv/atoms/icon';
import { UniversalMenuItem } from '@klv/molecules/universal-menu-item';
import { LanguageSelector } from '@klv/molecules/language-selector';
import { generateClassName } from '@klv/shared/utils/functions';
import { matchPath, matchUrl, setCurrentMenuItem } from './utils';

import * as styles from './styles/utilityNavigation.module.scss';

import type { IUtilityNavigation } from './types';

export const UtilityNavigation = ({
  children,
  className = '',
  menuItems = [],
  path = '',
  platformActions = [],
  scrollDirection,
  siteActions = [],
  siteMetadata,
  showSubnav = false,
  backgroundColor = 'charcoal',
  langCode,
  sites = [],
  translations = [],
  ...props
}: IUtilityNavigation) => {
  const isHydrated = useIsHydrated();
  const processedMenuItems = menuItems.map(({ url: href, ...rest }) => ({
    ...rest,
    href,
    pathMatch: href ? matchPath({ href, path, siteUrl: siteMetadata.siteUrl }) : false,
    urlMatch: href ? matchUrl({ href, siteParentUrl: siteMetadata.siteParentUrl, siteUrl: siteMetadata.siteUrl }) : false,
  }));
  const { i18n } = useTranslation();
  const pathMatchedMenuItem = processedMenuItems.find(({ pathMatch }) => pathMatch);
  const urlMatchedMenuItem = processedMenuItems.find(({ urlMatch }) => urlMatch);
  const { langCode: siteMetadataLangCode = '' } = siteMetadata || {};
  const currentLanguage = langCode?.toLowerCase() || i18n?.language?.toLowerCase() || siteMetadataLangCode?.toLowerCase();

  return (
    <div
      className={generateClassName([
        className,
        styles.utilityNavigation,
        scrollDirection === 'down' && styles.scrolled,
        showSubnav && styles.showSubnav,
      ])}
      {...props}
    >
      <div className={generateClassName(['grid', styles.grid])}>
        <div className={styles.container}>
          {processedMenuItems.length > 0 && (
            <nav className={styles.utility}>
              <ul className={styles.linkList}>
                {processedMenuItems.map(({ href, id, label, target }, index) => (
                  <li className={styles.listItem} key={id}>
                    <UniversalMenuItem
                      aria-current={isHydrated && setCurrentMenuItem({ id, index, pathMatchedMenuItem, urlMatchedMenuItem })}
                      className={styles.link}
                      href={href}
                      target={target}
                    >
                      {label}
                    </UniversalMenuItem>
                  </li>
                ))}
              </ul>
            </nav>
          )}
          {children && (
            <div className={styles.children}>
              {children}
            </div>
          )}
          <div className={styles.actions}>
            <div className={styles.languageSelectorContainer}>
              <LanguageSelector
                backgroundColor={backgroundColor}
                currentLanguage={currentLanguage}
                sites={sites}
                translations={translations}
                isUtilityNavigation={true}
              />
            </div>
            {siteActions.length > 0 && (
              <div className={generateClassName([styles.siteActions, !isHydrated && 'visuallyHidden'])}>
                <ul className={styles.linkList}>
                  {siteActions.map(({ href, icon, label }) => (
                    <li key={label}>
                      <UniversalMenuItem
                        aria-label={icon ? label : undefined}
                        className={styles.link}
                        href={href}
                      >
                        {icon ? <Icon color='currentColor' iconName={icon} size={1.6} /> : label}
                      </UniversalMenuItem>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <nav className={generateClassName([styles.platformActions, !isHydrated && 'visuallyHidden'])}>
              <ul className={styles.linkList}>
                {platformActions.map(({ component, href, label }) => (
                  <li key={label}>
                    {component ? component : (
                      <UniversalMenuItem
                        className={styles.link}
                        href={href}
                      >
                        {label}
                      </UniversalMenuItem>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};
